import { useCallback, useState, useRef, Dispatch, SetStateAction } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";
import { ConversationHistoryService } from "../../services/conversationHistoryService";
import type { HistoryMessage } from "../types/HistoryMessage";
import { ForbiddenError } from "../types/ConversationErrors";
import { showToast } from "../utils/toast";

const CACHE_DURATION_MS = 60 * 1000; // 1 minute

function processHistoryMessages(rawMessages: HistoryMessage[]): HistoryMessage[] {
  return rawMessages.map((message) => {
    if (message.type === "human") {
      return message;
    }
    if (message.type === "ai") {
      return {
        ...message,
        tool_calls: message.tool_calls || [],
        invalid_tool_calls: message.invalid_tool_calls || [],
      };
    }
    if (message.type === "tool") {
      const toolMsg = message as any;
      const toolName = toolMsg.tool_name || toolMsg.name;
      const status = toolMsg.status === "success" ? "completed" : toolMsg.status || "completed";
      return {
        ...message,
        tool_name: toolName,
        status,
        content:
          typeof message.content === "string" ? message.content : JSON.stringify(message.content),
        args: toolMsg.args,
      };
    }
    return message;
  });
}

export const useConversationHistory = (
  conversationId: string | null,
  setMessages: Dispatch<SetStateAction<HistoryMessage[]>>
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"not_found" | "forbidden" | null>(null);
  const authenticatedFetch = useAuthenticatedFetch();
  const lastLoadRef = useRef<{
    conversationId: string | null;
    timestamp: number;
  }>({
    conversationId: null,
    timestamp: 0,
  });

  const loadHistory = useCallback(
    async (overrideConversationId?: string | null) => {
      const targetId = overrideConversationId !== undefined ? overrideConversationId : conversationId;
      if (!targetId) return;

      const now = Date.now();
      const lastLoad = lastLoadRef.current;

      if (lastLoad.conversationId === targetId && now - lastLoad.timestamp < CACHE_DURATION_MS) {
        return;
      }

      setLoading(true);
      try {
        setError(null);
        const history = await ConversationHistoryService.getHistory(targetId, authenticatedFetch);

        const normalizedMessages = processHistoryMessages(history.messages);
        setMessages(normalizedMessages);
        lastLoadRef.current = { conversationId: targetId, timestamp: now };
      } catch (err) {
        if (err instanceof ForbiddenError) {
          setError("forbidden");
        } else if (err instanceof Error && err.message.includes("404")) {
          setError("not_found");
        } else if (err instanceof Error) {
          showToast(err.message, "error");
        } else {
          showToast("Une erreur est survenue lors du chargement de l'historique", "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [conversationId, authenticatedFetch, setMessages]
  );

  return {
    loading,
    error,
    loadHistory,
  };
};
